import { useRef } from 'react';
import {
  ASSET_INTERACTION_CLASS,
  AUDIO_FALLBACK_IMAGE_16X9_SRC,
  AUDIO_FALLBACK_IMAGE_1X1_SRC,
  FULL_PLAYER_ARTWORK_LABEL_GAP_DESKTOP,
  FULL_PLAYER_ARTWORK_SIZE_DESKTOP,
  PLAYBACK_CONTROL_BUTTON_CLASS,
  PLAYBACK_CONTROL_SIZE_MOBILE,
} from './audio-player-constants';
import { clamp, hasVisibleText } from './audio-player-utils';
import { IconButton } from './icon-button';
import { PlaybackSpeed } from './playback-speed';
import type { ExpandedAudioPlayerProps } from './types';

/** Renders the expanded full-screen player experience for mobile and desktop. */
export function ExpandedAudioPlayer({
  entry,
  imageSrc,
  playlistName,
  title,
  artist,
  isPlaying,
  currentTime,
  duration,
  remaining,
  playbackRate,
  speedOptions,
  hasSpeedControl,
  hasQueue,
  isMuted,
  volume,
  isQueueOpen,
  isSpeedMenuOpen,
  isFavorited,
  isPlaylistAdded,
  showFavoriteMobile,
  iconAssets,
  queueTitle,
  progressColor,
  totalBarColor,
  handleColor,
  elapsedLabelColor,
  totalLabelColor,
  fullImageCornerRadius,
  fullImagePlaceholderColor,
  fullImageBorderColor,
  fullContainerClassName,
  fullTextGutter,
  titleTextStyle,
  artistTextStyle,
  playlistTextStyle,
  timeLabelTextStyle,
  formatTime,
  onPrevious,
  onTogglePlayback,
  onNext,
  onSkipBack,
  onSkipAhead,
  onToggleMute,
  onToggleFavorite,
  onToggleAddToPlaylist,
  onToggleQueue,
  onToggleSpeedMenu,
  onOpenMoreMenu,
  onSelectPlaybackRate,
  onVolumeChange,
  onClose,
  onSeek,
  playbackSpeedOpen,
  setPlaybackSpeedOpen,
  isExpanded,
}: ExpandedAudioPlayerProps): JSX.Element {
  const mobileMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const desktopMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const clampedProgress = clamp(progressPercent, 0, 100);
  const headerTitle = playlistName || title;
  const mobileArtworkSrc = imageSrc || AUDIO_FALLBACK_IMAGE_16X9_SRC;
  const desktopArtworkSrc = imageSrc || AUDIO_FALLBACK_IMAGE_1X1_SRC;

  const mobileHorizontalPadding = 24;
  const mobileHeaderPaddingTop = 20;
  const mobileHeaderPaddingBottom = 0;
  const mobileContentPaddingBottom = 20;
  const mobileBottomActionsPaddingBottom = 20;
  const mobileHeaderIconsGap = 8;
  const mobileMetadataMarginTop = 16;
  const mobileControlsMarginTop = 16;
  const mobileProgressMarginTop = 16;
  const mobileHeaderIconSize = 24;
  const mobileProgressTrackHeight = 3;
  const mobileProgressThumbSize = 12;

  return (
    <>
      <div
        className={`pointer-events-auto fixed bottom-0 left-0 right-0 z-[9999] flex h-full transform flex-col
  text-[#efefef] transition-transform duration-300 ease-out desktop:hidden
      ${fullContainerClassName}
      ${isExpanded ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div
          data-testid="expanded-mobile-top-bar"
          className={`flex items-center justify-between`}
          style={{
            paddingLeft: mobileHorizontalPadding,
            paddingRight: mobileHorizontalPadding,
            paddingTop: mobileHeaderPaddingTop,
            paddingBottom: mobileHeaderPaddingBottom,
          }}
        >
          <p
            className="min-w-0 truncate font-semibold text-audio-player-full-playlist-mobile desktop:text-audio-player-full-playlist-desktop"
            style={playlistTextStyle}
          >
            {headerTitle}
          </p>

          <div
            className="ml-4 flex shrink-0 items-center"
            style={{ gap: mobileHeaderIconsGap }}
          >
            <IconButton
              buttonRef={mobileMoreButtonRef}
              src={iconAssets.more}
              alt="More"
              onClick={() => onOpenMoreMenu?.(mobileMoreButtonRef.current)}
              size={mobileHeaderIconSize}
              buttonClassName="group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active"
              iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
              iconColor={'var(--action_button_asset_color_default)'}
            />
            <IconButton
              src={iconAssets.minimize}
              alt="Minimize"
              onClick={onClose}
              size={mobileHeaderIconSize}
              buttonClassName="group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active"
              iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
              iconColor={'var(--action_button_asset_color_default)'}
            />
          </div>
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
          style={{
            paddingLeft: mobileHorizontalPadding,
            paddingRight: mobileHorizontalPadding,
            paddingBottom: mobileContentPaddingBottom,
          }}
        >
          <div
            data-testid="expanded-mobile-center-stack"
            className="my-auto flex w-full shrink-0 flex-col"
          >
            <div className="flex w-full justify-center">
              <div
                data-testid="expanded-mobile-artwork"
                className="aspect-square w-full max-w-[321px] shrink-0 overflow-hidden border tablet:max-w-[400px]"
                style={{
                  borderRadius: fullImageCornerRadius,
                  backgroundColor: fullImagePlaceholderColor,
                  borderColor: fullImageBorderColor,
                }}
              >
                <img
                  data-testid="expanded-mobile-artwork-image"
                  src={mobileArtworkSrc}
                  alt={entry.title}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            <div
              data-testid="expanded-mobile-metadata"
              className="mx-auto w-full max-w-[321px] tablet:max-w-[400px]"
              style={{
                marginTop: mobileMetadataMarginTop,
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  {hasVisibleText(title) ? (
                    <h2
                      className="truncate font-bold text-audio-player-full-title-mobile desktop:text-audio-player-full-title-desktop"
                      style={titleTextStyle}
                    >
                      {title}
                    </h2>
                  ) : null}
                  {hasVisibleText(artist) ? (
                    <p
                      className="truncate text-audio-player-full-artist-mobile desktop:text-audio-player-full-artist-desktop"
                      style={artistTextStyle}
                    >
                      {artist}
                    </p>
                  ) : null}
                </div>

                {showFavoriteMobile ? (
                  <IconButton
                    src={
                      isFavorited ? iconAssets.favorited : iconAssets.favorite
                    }
                    alt={isFavorited ? 'Unfavorite' : 'Favorite'}
                    onClick={onToggleFavorite}
                    size={mobileHeaderIconSize}
                    buttonClassName="group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active"
                    iconClassName={`audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
                    iconColor={'var(--action_button_asset_color_default)'}
                  />
                ) : null}
              </div>
            </div>

            <div
              data-testid="expanded-mobile-controls"
              className="mx-auto flex w-full max-w-[321px] items-center justify-between tablet:max-w-[400px]"
              style={{
                marginTop: mobileControlsMarginTop,
              }}
            >
              <IconButton
                src={iconAssets.previous}
                alt="Skip backward"
                onClick={onPrevious}
                size={PLAYBACK_CONTROL_SIZE_MOBILE.skipTrack}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconClassName={ASSET_INTERACTION_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={iconAssets.skipBack}
                alt="Rewind"
                onClick={onSkipBack}
                size={PLAYBACK_CONTROL_SIZE_MOBILE.seek}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconClassName={ASSET_INTERACTION_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={isPlaying ? iconAssets.pause : iconAssets.play}
                alt={isPlaying ? 'Pause' : 'Play'}
                onClick={onTogglePlayback}
                size={PLAYBACK_CONTROL_SIZE_MOBILE.playPause}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconClassName={ASSET_INTERACTION_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={iconAssets.skipAhead}
                alt="Fast-forward"
                onClick={onSkipAhead}
                size={PLAYBACK_CONTROL_SIZE_MOBILE.seek}
                iconClassName={ASSET_INTERACTION_CLASS}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
              <IconButton
                src={iconAssets.next}
                alt="Skip forward"
                onClick={onNext}
                size={PLAYBACK_CONTROL_SIZE_MOBILE.skipTrack}
                buttonClassName={PLAYBACK_CONTROL_BUTTON_CLASS}
                iconClassName={ASSET_INTERACTION_CLASS}
                iconColor={'white'}
                preserveSvgColor={true}
              />
            </div>

            <div
              data-testid="expanded-mobile-progress"
              className="mx-auto w-full max-w-[321px] tablet:max-w-[400px]"
              style={{
                marginTop: mobileProgressMarginTop,
              }}
            >
              <div className="relative h-5 w-full">
                <div
                  className="absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    height: mobileProgressTrackHeight,
                    backgroundColor: totalBarColor,
                  }}
                />
                <div
                  className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    height: mobileProgressTrackHeight,
                    width: `${clampedProgress}%`,
                    backgroundColor: progressColor,
                  }}
                />
                <div
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    left: `${clampedProgress}%`,
                    width: mobileProgressThumbSize,
                    height: mobileProgressThumbSize,
                    backgroundColor: handleColor,
                  }}
                />

                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.5}
                  value={currentTime}
                  onChange={onSeek}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Seek"
                />
              </div>

              <div
                className="mt-1 flex justify-between tabular-nums"
                style={timeLabelTextStyle}
              >
                <span style={{ color: elapsedLabelColor }}>
                  {formatTime(currentTime)}
                </span>
                <span style={{ color: totalLabelColor }}>
                  - {formatTime(remaining)}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          data-testid="expanded-mobile-bottom-actions"
          className="flex shrink-0 items-center justify-between"
          style={{
            paddingLeft: mobileHorizontalPadding,
            paddingRight: mobileHorizontalPadding,
            paddingBottom: mobileBottomActionsPaddingBottom,
          }}
        >
          <PlaybackSpeed
            playbackRate={playbackRate}
            speedOptions={speedOptions}
            playbackSpeedOpen={playbackSpeedOpen}
            setPlaybackSpeedOpen={setPlaybackSpeedOpen}
            onSelectPlaybackRate={onSelectPlaybackRate}
            iconAssets={iconAssets}
            isMobileFullPlayer={true}
          />
          <IconButton
            src={iconAssets.queue}
            alt={isQueueOpen ? 'Hide queue' : 'Show queue'}
            onClick={onToggleQueue}
            size={mobileHeaderIconSize}
            iconColor={'var(--action_button_asset_color_default)'}
            buttonClassName={`group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
            iconClassName={`h-[24px] w-[24px] audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
          />
        </div>
      </div>

      {/* Desktop Full Player */}

      <div
        data-testid="expanded-desktop-shell"
        className={`fixed bottom-0 left-0 right-0 z-[100] hidden h-full transform flex-col text-[#efefef]
    transition-transform duration-300 ease-out desktop:flex
    ${fullContainerClassName}
    ${isQueueOpen ? 'desktop:backdrop-blur-0' : ''}
    ${
      isExpanded
        ? 'pointer-events-auto translate-y-0'
        : 'pointer-events-none translate-y-full'
    }`}
      >
        <div className="absolute right-5 top-5 flex items-center gap-4">
          <IconButton
            buttonRef={desktopMoreButtonRef}
            src={iconAssets.more}
            alt="More"
            onClick={() => onOpenMoreMenu?.(desktopMoreButtonRef.current)}
            size={44}
            iconSize={24}
            buttonClassName="group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active"
            iconClassName="h-6 w-6 audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active"
            iconColor={'var(--action_button_asset_color_default)'}
          />
          <IconButton
            src={iconAssets.minimize}
            alt="Minimize"
            onClick={onClose}
            size={44}
            iconSize={24}
            buttonClassName="group flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active"
            iconClassName="h-6 w-6 audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active"
            iconColor={'var(--action_button_asset_color_default)'}
          />
        </div>

        <div
          data-testid="expanded-desktop-center-panel"
          className={`mx-auto flex h-full w-full max-w-6xl items-center justify-center px-4 py-10 tablet:px-5 tablet:py-12 desktop:px-12 desktop:py-16 ${
            isQueueOpen ? 'desktop:pr-[362px]' : ''
          }`}
        >
          <div
            data-testid="expanded-desktop-stack"
            className="flex flex-col items-center text-center"
            style={{
              width: FULL_PLAYER_ARTWORK_SIZE_DESKTOP,
              gap: FULL_PLAYER_ARTWORK_LABEL_GAP_DESKTOP,
            }}
          >
            <div
              data-testid="expanded-desktop-artwork"
              className="shrink-0 overflow-hidden border shadow-2xl shadow-black/70"
              style={{
                width: FULL_PLAYER_ARTWORK_SIZE_DESKTOP,
                height: FULL_PLAYER_ARTWORK_SIZE_DESKTOP,
                borderRadius: fullImageCornerRadius,
                backgroundColor: fullImagePlaceholderColor,
                borderColor: fullImageBorderColor,
              }}
            >
              <img
                data-testid="expanded-desktop-artwork-image"
                src={desktopArtworkSrc}
                alt={entry.title}
                className="h-full w-full object-cover"
              />
            </div>

            <div
              data-testid="expanded-desktop-metadata"
              className="flex w-full min-w-0 flex-col"
              style={{ gap: fullTextGutter }}
            >
              {playlistName ? (
                <p
                  className="text-audio-player-full-playlist-mobile desktop:text-audio-player-full-playlist-desktop"
                  style={playlistTextStyle}
                >
                  {playlistName}
                </p>
              ) : null}
              {hasVisibleText(title) ? (
                <h2
                  className="font-semibold text-audio-player-full-title-mobile desktop:text-audio-player-full-title-desktop"
                  style={titleTextStyle}
                >
                  {title}
                </h2>
              ) : null}
              {hasVisibleText(artist) ? (
                <p
                  className="text-audio-player-full-artist-mobile desktop:text-audio-player-full-artist-desktop"
                  style={artistTextStyle}
                >
                  {artist}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
